/**
 * A profile photo, made small in the browser before it is sent.
 *
 * The server keeps photos in the database and refuses anything over about
 * 300 KB, so a 12-megapixel phone photo is cut down here: cropped to the
 * middle square and drawn at `size` pixels, as a JPEG. A transparent PNG gets
 * a white ground, because JPEG has no transparency and would make it black.
 */

export async function squarePhoto(file: File, size = 256): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      // An Event, not an Error, is what the browser gives; say what it means.
      // An iPhone's HEIC photo lands here in most browsers.
      el.onerror = () =>
        reject(new Error('That file could not be read as a picture. Try a JPEG or PNG.'));
      el.src = url;
    });

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('This browser could not resize the photo.');

    const side = Math.min(img.naturalWidth, img.naturalHeight);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(
      img,
      (img.naturalWidth - side) / 2,
      (img.naturalHeight - side) / 2,
      side,
      side,
      0,
      0,
      size,
      size
    );
    return canvas.toDataURL('image/jpeg', 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}
